/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ListView, ListViewRow } from 'model/state';
import { Location, typeToString, LocationType, isLocationFullAccess, systemModeToString, SystemMode } from 'model';
import { Link } from 'react-router-dom';
import { AlertsBadge, FloCheckbox, FloList, FloMenuButtonItemType } from 'components';
import styles from './LocationList.module.css';
import { FloIconMenu } from 'components/FloIconMenu';


type LocationHeaderProps = {
  isSelectedAll:    boolean,
  setSelectAll:     (selectAll: boolean) => void,
  parent:           Location | null,
};

const LocationHeader: React.FC<LocationHeaderProps> = ({isSelectedAll, setSelectAll, parent}) => {
  const { t } = useTranslation();
  return (
    <>
      { !parent ? // ROOT LOCATION VIEW
        <>
          <Col xs="5" md="4" xl="5">
            <FloCheckbox className={styles.checkbox} checked={isSelectedAll} onChange={() => setSelectAll(!isSelectedAll)} />
            <span>{t('Name/Address')}</span>
          </Col>
          <Col        md="3" xl="3" className="d-none d-md-block">{t('City, State, Country')}</Col>
          <Col xs="2" md="2" xl="2">{t('Type')}</Col>
          <Col xs="2" md="1" xl="1">{t('Mode')}</Col>
          <Col xs="3" md="2" xl="1">{t('Alerts')}</Col>
        </>
        : (parent.type === LocationType.REGION) ? // COMMUNITY view
        <>
          <Col xs="5" md="4" xl="4">
            <FloCheckbox className={styles.checkbox} checked={isSelectedAll} onChange={() => setSelectAll(!isSelectedAll)} />
            <span>{t('Name/Address')}</span>
          </Col>
          <Col        md="2" xl="3" className="d-none d-md-block">{t('City')}</Col>
          <Col        md="1" xl="1" className="d-none d-md-block">{t('ZIP')}</Col>
          <Col xs="2" md="1" xl="1">{t('Type')}</Col>
          <Col xs="2" md="1" xl="1">{t('Mode')}</Col>
          <Col        md="1" xl="1" className="d-none d-md-block">{t('Units')}</Col>
          <Col xs="3" md="2" xl="1">{t('Alerts')}</Col>
        </> : // PROPERTY VIEW
        <>
          <Col xs="6" md="8">
            <FloCheckbox className={styles.checkbox} checked={isSelectedAll} onChange={() => setSelectAll(!isSelectedAll)} />
            <span>{t('Unit #')}</span>
          </Col>
          <Col xs="2" md="2">{t('Mode')}</Col>
          <Col xs="4" md="2">{t('Alerts')}</Col>
        </>
      }
    </>
  )
}

type LocationLinkProps = {
  location: ListViewRow<Location>,
};

const LocationLink: React.FC<LocationLinkProps> = ({ location }) => (
  <span><Link to={`/${location.type === LocationType.UNIT ? 'unit' : 'locations'}/${location.id}`}>{location.nickname || `${location.address}${location.address2 ? `, ${location.address2}` : ''}`}</Link></span>
)

type ActionColumnProps = {
  location: ListViewRow<Location>,
  onEdit:                 (location: Location) => void,
  onSetThresholdsClick:       (location: Location) => void,
  onAutoShutoffClick:          (location: Location) => void,
  onSetAlarmPreferencesClick: (location: Location) => void,
  onAddToClick:               (type: LocationType, location: Location) => void
  onRemoveClick:              (location: Location) => void,
  onLocationSleepClick:       (hours: number, location: Location) => void,
  onLocationChangeModeClick:  (target: SystemMode, location: Location) => void,
};

const ActionColumn: React.FC<ActionColumnProps> = React.memo(({
  location,
  onEdit,
  onSetThresholdsClick,
  onAutoShutoffClick,
  onSetAlarmPreferencesClick,
  onAddToClick,
  onRemoveClick,
  onLocationSleepClick,
  onLocationChangeModeClick,
}) => {
  const { t } = useTranslation();
  const disableMenuItems = !isLocationFullAccess(location);

  return (
    <>
      <AlertsBadge
        className = {styles.badge}
        stats = {location.notifications?.pending || { criticalCount: 0, infoCount: 0, warningCount: 0 }}
      />
      <FloIconMenu
        className = {styles.iconMenu}
        menuItems = {[
          { id: 'edit-location', title: t('Edit Location'), onClick: () => onEdit(location), disabled: disableMenuItems },
          { id: 'divider', title: 'divider', type: FloMenuButtonItemType.DIVIDER },
          { id: 'set-thresholds', onClick: () => onSetThresholdsClick(location), title: t('add-to-set-thresholds'), disabled: disableMenuItems },
          { id: 'auto-shutoff', onClick: () => onAutoShutoffClick(location), title: t('Auto Shutoff'), disabled: disableMenuItems },
          { id: 'set-alarm-preferences', onClick: () => onSetAlarmPreferencesClick(location), title: t('set-alarm-preferences') },
          { id: 'add-to-property', onClick: () => onAddToClick(LocationType.PROPERTY, location), title: t('Add to Property'), disabled: disableMenuItems },
          { id: 'add-to-building', onClick: () => onAddToClick(LocationType.BUILDING, location), title: t('Add to Building'), disabled: disableMenuItems },
          { id: 'add-to-community', onClick: () => onAddToClick(LocationType.REGION, location), title: t('Add to Community'), disabled: disableMenuItems },
          { id: 'remove', onClick: () => onRemoveClick(location), title: t('add-to-remove-items'), disabled: disableMenuItems},
          { id: 'divider', title: 'divider', type: FloMenuButtonItemType.DIVIDER },
          { id: 'sleep-24', title: t('Sleep 24 hours'), onClick: () => onLocationSleepClick(24, location), disabled: disableMenuItems },
          { id: 'sleep-72', title: t('Sleep 72 hours'), onClick: () => onLocationSleepClick(72, location), disabled: disableMenuItems },
          { id: 'home-mode', title: t('Set to Home Mode'), onClick: () => onLocationChangeModeClick(SystemMode.HOME, location), disabled: disableMenuItems },
          { id: 'away-mode', title: t('Set to Away Mode'), onClick: () => onLocationChangeModeClick(SystemMode.AWAY, location), disabled: disableMenuItems },
        ]}
        horizontal
      />
    </>
  )
})

type SelectableColumnProps = {
  location: ListViewRow<Location>,
  setSelectRow: (selectAll: boolean, locationId: string) => void,
};

const SelectableColumn: React.FC<SelectableColumnProps> = React.memo(({ location, setSelectRow }) => {
  return (
    <>
      <div className={styles.checkBoxWrapper}>
        <FloCheckbox className={styles.checkbox} checked={location.isSelected} noMargin onChange={() => setSelectRow(!location.isSelected, location.id || '')} />
      </div>
      <div className={styles.titleWrapper}>
        <div><LocationLink location={location} /></div>
        { location.nickname && location.address && <div>{`${location.address}${location.address2 ? `, ${location.address2}` : ''}`}</div> }
      </div>
    </>
  )
})

type LocationRowProps = {
  location:                   ListViewRow<Location>,
  setSelectRow:               (selectAll: boolean, locationId: string) => void,
  parent:                     Location | null,
  onEdit:                     (location: Location) => void,
  onSetThresholdsClick:       (location: Location) => void,
  onAutoShutoffClick:          (location: Location) => void,
  onSetAlarmPreferencesClick: (location: Location) => void,
  onAddToClick:               (type: LocationType, location: Location) => void
  onRemoveClick:              (location: Location) => void,
  onLocationSleepClick:       (hours: number, location: Location) => void,
  onLocationChangeModeClick:  (target: SystemMode, location: Location) => void,
}

const LocationRow: React.FC<LocationRowProps> = React.memo(({
  location,
  setSelectRow,
  parent,
  onEdit,
  onSetThresholdsClick,
  onAutoShutoffClick,
  onSetAlarmPreferencesClick,
  onAddToClick,
  onRemoveClick,
  onLocationSleepClick,
  onLocationChangeModeClick,
}) => {
  return (
    <>
      { !parent ? // ROOT LOCATION VIEW
        <>
          <Col xs="5" md="4" xl="5" className={styles.firstColumn}>
            <SelectableColumn location={location} setSelectRow={setSelectRow} />
          </Col>
          <Col        md="3" xl="3" className="d-none d-md-block">{`${location?.city ? `${location.city}, ` : ''}${location?.state ? `${location?.state?.toUpperCase() ?? ''}, `: '' }${location?.country?.toUpperCase() ?? ''}`}</Col>
          <Col xs="2" md="2" xl="2">{`${typeToString(location.type)}${location.children.length ? ` (${location.children.length})` : ''}`}</Col>
          <Col xs="2" md="1" xl="1">{systemModeToString(location.systemMode?.target || SystemMode.HOME)}</Col>
          <Col xs="3" md="2" xl="1" className={styles.actionColumn}>
            <ActionColumn
              location={location}
              onEdit={onEdit}
              onSetThresholdsClick={onSetThresholdsClick}
              onAutoShutoffClick={onAutoShutoffClick}
              onSetAlarmPreferencesClick={onSetAlarmPreferencesClick}
              onAddToClick={onAddToClick}
              onRemoveClick={onRemoveClick}
              onLocationSleepClick={onLocationSleepClick}
              onLocationChangeModeClick={onLocationChangeModeClick}
            />
          </Col>
        </>
        : (parent.type === LocationType.REGION) ? // COMMUNITY view
        <>
          <Col xs="5" md="4" xl="4" className={styles.firstColumn}>
            <SelectableColumn location={location} setSelectRow={setSelectRow} />
          </Col>
          <Col        md="2" xl="3" className="d-none d-md-block">{location.city}</Col>
          <Col        md="1" xl="1" className="d-none d-md-block">{location.postalCode}</Col>
          <Col xs="2" md="1" xl="1">{typeToString(location.type)}</Col>
          <Col xs="2" md="1" xl="1">{systemModeToString(location.systemMode?.target || SystemMode.HOME)}</Col>
          <Col        md="1" xl="1" className="d-none d-md-block">{location.type === LocationType.PROPERTY ? location.children.length ? location.children.length : '0' : ''}</Col>
          <Col xs="3" md="2" xl="1" className={styles.actionColumn}>
            <ActionColumn
              location={location}
              onEdit={onEdit}
              onSetThresholdsClick={onSetThresholdsClick}
              onAutoShutoffClick={onAutoShutoffClick}
              onSetAlarmPreferencesClick={onSetAlarmPreferencesClick}
              onAddToClick={onAddToClick}
              onRemoveClick={onRemoveClick}
              onLocationSleepClick={onLocationSleepClick}
              onLocationChangeModeClick={onLocationChangeModeClick}
            />
          </Col>
        </> : // PROPERTY VIEW
        <>
          <Col xs="6" md="8" className={styles.firstColumn}>
            <SelectableColumn location={location} setSelectRow={setSelectRow} />
          </Col>
          <Col xs="2" md="2">{systemModeToString(location.systemMode?.target || SystemMode.HOME)}</Col>
          <Col xs="4" md="2" className={styles.actionColumn}>
            <ActionColumn
              location={location}
              onEdit={onEdit}
              onSetThresholdsClick={onSetThresholdsClick}
              onAutoShutoffClick={onAutoShutoffClick}
              onSetAlarmPreferencesClick={onSetAlarmPreferencesClick}
              onAddToClick={onAddToClick}
              onRemoveClick={onRemoveClick}
              onLocationSleepClick={onLocationSleepClick}
              onLocationChangeModeClick={onLocationChangeModeClick}
            />
          </Col>
        </>
      }
    </>
  )
})


type LocationListProps = {
  parentId?:                  string;
  locationList:               ListView<Location>,
  isSelectedAll:              boolean,
  setSelectAll:               (selectAll: boolean) => void,
  setSelectRow:               (selectAll: boolean, locationId: string) => void,
  parent:                     Location | null,
  onEdit:                     (location: Location) => void,
  onSetThresholdsClick:       (location: Location) => void,
  onAutoShutoffClick:          (location: Location) => void,
  onSetAlarmPreferencesClick: (location: Location) => void,
  onAddToClick:               (type: LocationType, location: Location) => void
  onRemoveClick:              (location: Location) => void,
  onLocationSleepClick:       (hours: number, location: Location) => void,
  onLocationChangeModeClick:  (target: SystemMode, location: Location) => void,
}

export const LocationList: React.FC<LocationListProps> = React.memo(({
  locationList,
  isSelectedAll,
  setSelectAll,
  setSelectRow,
  parent,
  onEdit,
  onSetThresholdsClick,
  onAutoShutoffClick,
  onSetAlarmPreferencesClick,
  onAddToClick,
  onRemoveClick,
  onLocationSleepClick,
  onLocationChangeModeClick,
}) => {
  return (
    <FloList>
      <FloList.Header>
        <LocationHeader isSelectedAll={isSelectedAll} setSelectAll={setSelectAll} parent={parent} />
      </FloList.Header>
      <FloList.Body>
          {
            locationList.rows?.map(location =>
              <FloList.Row key={location.id}>
                <LocationRow
                  location={location}
                  setSelectRow={setSelectRow}
                  parent={parent}
                  onEdit={onEdit}
                  onSetThresholdsClick={onSetThresholdsClick}
                  onAutoShutoffClick={onAutoShutoffClick}
                  onSetAlarmPreferencesClick={onSetAlarmPreferencesClick}
                  onAddToClick={onAddToClick}
                  onRemoveClick={onRemoveClick}
                  onLocationSleepClick={onLocationSleepClick}
                  onLocationChangeModeClick={onLocationChangeModeClick}
                />
              </FloList.Row>)
          }
      </FloList.Body>
    </FloList>
  )
})
